import { Field } from '../../components/table'
import React, { useEffect, useState } from 'react'
import { Actions, Pager } from '../../components/table/types'
import editIcon from '../../assets/table_icons/edit.svg'
import deleteIcon from '../../assets/table_icons/delete.svg'
import { useTranslation } from 'react-i18next'
import { getContentContainer } from '../../container/treatment-module'
import { IUserTreatmentService, USER_TREATMENT_SERVICE_KEY } from '../../modules/treatments'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import { UserTreatmentQuery } from '../../modules/treatments/models/UserTreatment'
import { UserTreatmentWithTreatmentName } from '../../modules/treatments/models/UserTreatmentWithTreatmentName'
import { dateToDateString } from '../../common/utils/date'
import { reduceString } from '../../common/utils/strings'
import { Query, QueryParam } from '../../common/api/Query'
import { ROUTE_TREATMENTS } from '../../routes/routes-constants'
import { navigate } from '@reach/router'
import { CustomModal } from '../../components/modal/CustomModal'
import { Box, Modal, Table as TableTreatment, TableContainer } from '@mui/material'
import { UnitType, unitTypeTypes } from '../../modules/treatments/enums/UnitType'
import { HeadTreatment } from '../../components/table/HeadTreatment'
import { BodyTreatment } from '../../components/table/BodyTreatment'

type TreatmentTableProps = {
  id?: string
}

const userContainer = getUserContainer()
const userTreatmentService = getContentContainer().get<IUserTreatmentService>(
  USER_TREATMENT_SERVICE_KEY
)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function Table(props: TreatmentTableProps) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [treatments, setTreatments] = useState<UserTreatmentWithTreatmentName[]>([])
  const [treatmentsPerPage, setTreatmentsPerPage] = useState<number>(10)
  const [page, setPage] = useState<number>(0)
  const [pager, setPager] = useState<Pager>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [count, setCount] = useState<number>(0)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [currentTreatment, setCurrentTreatment] = useState<UserTreatmentWithTreatmentName>()
  const innerWidth = window.innerWidth

  useEffect(() => {
    if (!isLoading || !props.id) {
      return
    }
    userTreatmentService
      .getListWithTreatmentNameByUserID(
        new Query({
          query: [new QueryParam<UserTreatmentQuery>('userID', props.id)],
          pager: { limit: treatmentsPerPage, offset: page * treatmentsPerPage },
        })
      )
      .subscribe((res) => {
        setCount(res.count)
        setTreatments(res.items)
        setIsLoading(false)
      })
  }, [isLoading, props.id, page, treatmentsPerPage])

  const handlePaginationChange = (event: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number.isNaN(event.target.value)) {
      setTreatmentsPerPage(10)
      return
    }
    setTreatmentsPerPage(Number.parseInt(event.target.value))
  }

  useEffect(() => {
    setIsLoading(true)
    setPager({
      page,
      count,
      handleChangePage: handlePaginationChange,
      rowsPerPage: treatmentsPerPage,
      handleChangeRowsPerPage,
    })
  }, [page, count, treatmentsPerPage])

  const editTreatment = (t: UserTreatmentWithTreatmentName) => {
    setCurrentTreatment(t)
    navigate(`${ROUTE_TREATMENTS}/${t.userTreatment.id}`)
  }

  const removeTreatment = (t: UserTreatmentWithTreatmentName) => {
    setCurrentTreatment(t)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => setOpenDeleteModal(false)

  const handleDeleteTreatment = () => {
    if (currentTreatment?.userTreatment?.id)
      userTreatmentService
        .delete(currentTreatment.userTreatment.id)
        .subscribe((_) => setIsLoading(true))
    setOpenDeleteModal(false)
    setIsLoading(true)
  }

  const isCreator = (t: UserTreatmentWithTreatmentName): boolean =>
    t.userTreatment.creatorID === loggedUser?.id

  const fields: Field<UserTreatmentWithTreatmentName>[] = [
    {
      name: 'name',
      label: t('name'),
    },
    {
      name: 'userTreatment',
      label: t('startDate'),
      renderFunc: (f, i) => new Date(i.userTreatment.startDate).toLocaleDateString(),
    },
    {
      name: 'userTreatment',
      label: t('finishDate'),
      renderFunc: (f, i) => {
        if (i.userTreatment.currently) return t('currently')

        if (!i.userTreatment.endDate) return ''

        if (new Date(i.userTreatment.endDate) < new Date(1910, 1)) return t('currently')

        return dateToDateString(new Date(i.userTreatment.endDate))
      },
    },
    {
      name: 'dose',
      label: t('dosePerIntake'),
      renderFunc: (f, i) => {
        if (i.dose === undefined || i.unit === undefined) return ''
        const unit = i.unit as UnitType
        const totalDose = i.dose * i.userTreatment.quantity
        return `${totalDose} ${unitTypeTypes()[unit]}`
      },
    },
    {
      name: 'userTreatment',
      label: t('quantity'),
      renderFunc: (f, i) => i.userTreatment.quantity.toString(),
    },
    {
      name: 'userTreatment',
      label: t('frequency'),
      renderFunc: (f, i) => `${i.userTreatment.frequency} ${t('hours')}`,
    },
    {
      name: 'userTreatment',
      label: t('reason'),
      renderFunc: (f, i) => reduceString(i.userTreatment.reason || '', 100),
    },
  ]

  const actions: Actions<UserTreatmentWithTreatmentName> = {
    actionsColumn: t('actions'),
    items: [
      {
        handler: editTreatment,
        icon: editIcon,
        label: 'edit',
        hidden: (t) => !isCreator(t),
      },
      {
        handler: removeTreatment,
        icon: deleteIcon,
        label: 'delete',
        hidden: (t) => !isCreator(t),
      },
    ],
  }

  return (
    <Box>
      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <CustomModal
          title={t('deleteTreatment')}
          warningText={t('irreversibleTreatmentAction')}
          handleClose={handleCloseDeleteModal}
          handleSave={handleDeleteTreatment}
        />
      </Modal>
      <TableContainer
        sx={{
          maxHeight: '450px',
        }}
      >
        <TableTreatment
          sx={{
            border: 'none',
            background: 'transparent',
            margin: 0,
            minWidth: '100%',
            '& td': {
              border: 'none',
              '&:last-child': {
                border: 'none',
              },
              '&:first-child': {
                borderLeftWidth: 1,
                borderLeft: 'none',
                borderColor: 'var(--green-light)',
                color: 'var(--green-light)',
              },
            },
            '& tr': {
              border: 'none',
            },
            '& th': {
              border: 'none',
            },
          }}
          style={{ border: 'none' }}
        >
          <HeadTreatment fields={fields} actions={actions} />
          <BodyTreatment
            actions={actions}
            fields={fields}
            items={treatments}
            rowKeyField={'name'}
            isLoading={isLoading}
          />
        </TableTreatment>
      </TableContainer>
      {!isLoading && treatments.length <= 0 && (
        <Box>
          <h4>{t('withoutAssignedMedication')}</h4>
        </Box>
      )}
    </Box>
  )
}
